'use client';
import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSession as useAuthSession, signIn, signOut } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Button } from '@ui/button';
import { LoadingView } from '@web/components';
import {
  CUSTOMER_UUID_ERROR,
  REFRESH_ACCESS_TOKEN_ERROR,
} from '../ShopifyAuth/tokenExpiry';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { Redirect } from '../../router';
import { useToast } from '../../Toast';
import { LoginFailureContainer, LoginFailureMessage } from './styles';

const LoginFailure = ({ onRetry }) => (
  <LoginFailureContainer>
    <LoginFailureMessage>
      We couldn't finish signing you in. Please try again.
    </LoginFailureMessage>
    <Button onClick={onRetry}>Try again</Button>
  </LoginFailureContainer>
);

LoginFailure.propTypes = {
  onRetry: PropTypes.func.isRequired,
};

export const LoginFlowContainer = () => {
  const { data: session, status } = useAuthSession();
  const {
    isActive,
    isLead,
    isLoading: isCustomerLoading,
    error: customerError,
  } = useSessionCustomer();
  const { showToast } = useToast();
  const reporter = useReporter();
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = searchParams?.get('next');
  const goToOnboarding = useCallback(() => {
    router.push(`/onboarding/`);
    showToast({
      status: 'info',
      message: 'Complete the quiz and checkout to finish creating your account',
    });
  }, [router, showToast]);

  const isShopifyAuthenticated = session && status === 'authenticated';
  const authError = session?.error;
  const signInFailed =
    authError === CUSTOMER_UUID_ERROR ||
    authError === REFRESH_ACCESS_TOKEN_ERROR;
  const directToShopifyLogin = !isShopifyAuthenticated;

  let cleanedNext = next?.includes('/account/account') ? '/account/' : next;

  // validate cleanedNext
  if (typeof window !== 'undefined') {
    const ourURL = new URL(window.location.href).hostname;
    const validUrlRegex = new RegExp(
      /^(http(s):\/\/.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/,
    );
    // checks if the 'next' query param is a valid URL
    const isValidUrl = validUrlRegex.test(cleanedNext);
    // if it's a vlaid URL, but is not within our domain: ignore it so the user logs into their account
    if (isValidUrl && !cleanedNext.includes(ourURL)) cleanedNext = undefined;
  }

  useEffect(() => {
    if (directToShopifyLogin) {
      return signIn('shopify');
    }
  }, [directToShopifyLogin]);

  useEffect(() => {
    if (next?.includes('/account/account')) {
      reporter.error(new Error('next url has value with repeated account'));
    }
  }, [next, reporter]);

  // if the customer is a potential lead that's already
  // authenticated, redirect them back to onboarding
  useEffect(() => {
    if (!isCustomerLoading && !isActive && isLead && isShopifyAuthenticated) {
      goToOnboarding();
    }
  }, [
    isActive,
    isLead,
    isCustomerLoading,
    isShopifyAuthenticated,
    goToOnboarding,
  ]);

  // Drop the broken session and let the effect above start one Shopify login.
  // Signing in from here as well starts a second OAuth round trip.
  const retryLogin = useCallback(() => {
    signOut({ redirect: false });
  }, []);

  if (!isShopifyAuthenticated || (isCustomerLoading && !signInFailed)) {
    return <LoadingView />;
  }

  if (signInFailed || customerError) {
    return <LoginFailure onRetry={retryLogin} />;
  }

  if (isActive) {
    return <Redirect to={cleanedNext ?? '/account/'} />;
  }

  if (isLead) {
    return <LoadingView />;
  }

  return <LoginFailure onRetry={retryLogin} />;
};

LoginFlowContainer.displayName = 'LoginFlowContainer';
